/**
 * El control de certificación MITECO: un interruptor (periodo | fichero) y UNA lista.
 *
 * La lógica —qué vacía qué, y a qué campo del contrato va cada dimensión— vive en
 * `../certificacion.ts` y tiene sus tests. Aquí solo se pinta y se conecta.
 *
 * El interruptor va DENTRO de la etiqueta del desplegable, no como un control aparte con su propio
 * label: así ocupa una sola casilla de la fila de filtros y se lee como un filtro con dos modos, que
 * es lo que es. El `description` de una línea se mantiene, igual que en los otros filtros, porque es
 * lo que los deja alineados en la misma fila (ver el comentario en App.tsx).
 */
import { Group, MultiSelect, SegmentedControl, Text } from '@mantine/core';

import {
  cambiarDimension,
  ETIQUETA_DIMENSION,
  opcionesDeCertificacion,
  type DimensionCertificacion,
  type FiltroCertificacion as Filtro,
} from '../certificacion';
import type { Vocabulario } from '../vocabulario';

export function FiltroCertificacion({
  filtro,
  onCambiar,
  vocabulario,
  descripcion,
  nothingFoundMessage,
}: {
  filtro: Filtro;
  onCambiar: (filtro: Filtro) => void;
  vocabulario: Pick<Vocabulario, 'ficheros' | 'periodos'>;
  /** Texto bajo el desplegable. Lo decide quien sabe si la lista está completa (App). */
  descripcion: string;
  nothingFoundMessage?: string;
}) {
  const opciones = opcionesDeCertificacion(vocabulario, filtro.dimension);
  const etiqueta = ETIQUETA_DIMENSION[filtro.dimension];

  return (
    <MultiSelect
      label={
        <Group gap="xs" wrap="nowrap" component="span">
          <Text component="span" size="sm" fw={500}>
            Certificación MITECO por
          </Text>
          <SegmentedControl
            size="xs"
            aria-label="Filtrar la certificación MITECO por"
            value={filtro.dimension}
            onChange={(v) => onCambiar(cambiarDimension(filtro, v as DimensionCertificacion))}
            data={[
              { value: 'periodo', label: ETIQUETA_DIMENSION.periodo },
              { value: 'fichero', label: ETIQUETA_DIMENSION.fichero },
            ]}
          />
        </Group>
      }
      placeholder={`Todos los ${etiqueta.toLowerCase()}s`}
      description={descripcion}
      nothingFoundMessage={nothingFoundMessage}
      data={opciones}
      value={filtro.valores}
      onChange={(valores) => onCambiar({ ...filtro, valores })}
      searchable
      clearable
    />
  );
}
